import { useTranslation } from 'react-i18next';

import { CheckIcon, CloseIcon } from 'components/Icons';
import { Button, useMessage } from 'components/ui';
import { formatDamageId } from 'horizon/components/Damages/utils';
import {
  type AtlasGqlHorizonDamage,
  AtlasGqlConfirmationStatus,
  useSetHorizonDamageConfirmationStatusMutation,
} from 'types/atlas-graphql';

interface ConfirmDamageProps {
  damage: AtlasGqlHorizonDamage;
  refetchQueries?: string[];
}

export const ConfirmDamage: React.FunctionComponent<ConfirmDamageProps> = ({
  damage,
  refetchQueries,
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [setConfirmationStatus, { loading }] = useSetHorizonDamageConfirmationStatusMutation({
    refetchQueries,
    onCompleted: data => {
      const damageId = data.setHorizonDamageConfirmationStatus?.id;
      const confirmationStatus = data.setHorizonDamageConfirmationStatus?.confirmationStatus;

      const label = formatDamageId({ id: damageId });
      message.success(
        confirmationStatus === AtlasGqlConfirmationStatus.Confirmed
          ? t('damage.damageConfirmedSuccess', 'Damage {{label}} confirmed.', { label })
          : t('damage.damageUnconfirmedSuccess', 'Damage {{label}} unconfirmed.', { label })
      );
    },
  });

  const handleSetConfirmationStatus = (confirmationStatus: AtlasGqlConfirmationStatus) => {
    setConfirmationStatus({
      variables: {
        input: {
          id: damage.id,
          confirmationStatus,
        },
      },
    });
  };

  return (
    <>
      {damage.confirmationStatus === AtlasGqlConfirmationStatus.Confirmed ? (
        <Button
          data-testid="unconfirm-damage-button"
          icon={<CloseIcon />}
          onClick={() => handleSetConfirmationStatus(AtlasGqlConfirmationStatus.NotConfirmed)}
          loading={loading}
        >
          {t('damage.unconfirmDamage', 'Unconfirm Damage')}
        </Button>
      ) : (
        <Button
          data-testid="confirm-damage-button"
          icon={<CheckIcon />}
          onClick={() => handleSetConfirmationStatus(AtlasGqlConfirmationStatus.Confirmed)}
          loading={loading}
        >
          {t('damage.confirmDamage', 'Confirm Damage')}
        </Button>
      )}
    </>
  );
};
